import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Users } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useSeo } from "../lib/seo";
import { COUNTRIES } from "../constants/options";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TYPES = ["TV drama", "TV entertainment", "Feature film", "Short film", "Commercial / advert", "Music video", "Other"];
const EMPTY = { contact_name: "", company: "", email: "", phone: "", production_title: "", production_type: "", country: "United Kingdom", location: "", shoot_dates: "", extras_needed: "", requirements: "", pay_rate: "", respond_by: "", respond_by_date: "" };

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="label-xs">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const Sent = () => (
  <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="casting-request-success">
    <CheckCircle2 className="mx-auto text-primary" size={56} />
    <h1 className="font-display text-4xl font-bold mt-8">Request received</h1>
    <p className="text-white/60 mt-4">We'll put your casting call out to our members in that area and send you details of people who are available for your project.</p>
  </div>
);

export default function CastingRequest() {
  useSeo("Request Extras for Your Production | extras.tv", "Assistant directors and casting teams: tell us how many extras you need, where and when, and we'll send details of available members.");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const inp = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/casting-requests", { ...form, extras_needed: Number(form.extras_needed) });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) return <Sent />;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16" data-testid="casting-request-page">
      <p className="label-xs text-primary flex items-center gap-2"><Users size={12} /> For assistant directors & casting teams</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[0.95]">Need a crowd? Tell us what you need.</h1>
      <p className="text-white/60 mt-5 max-w-2xl">Rather than searching individual profiles, send us one request. We'll put it out to our members in that location and send you details of the people who are available for your project. Free for productions.</p>
      <form onSubmit={submit} className="mt-12 space-y-10" data-testid="casting-request-form">
        <section className="grid md:grid-cols-2 gap-5">
          <p className="md:col-span-2 font-display text-xl">Your details</p>
          <F label="Your name *"><Input required {...inp("contact_name")} data-testid="cr-name-input" /></F>
          <F label="Production company"><Input {...inp("company")} data-testid="cr-company-input" /></F>
          <F label="Email *"><Input required type="email" {...inp("email")} data-testid="cr-email-input" /></F>
          <F label="Phone"><Input {...inp("phone")} data-testid="cr-phone-input" /></F>
        </section>
        <section className="grid md:grid-cols-2 gap-5">
          <p className="md:col-span-2 font-display text-xl">The production</p>
          <F label="Production title (if it can be shared)"><Input {...inp("production_title")} data-testid="cr-title-input" /></F>
          <F label="Type of production">
            <Select value={form.production_type || undefined} onValueChange={(v) => setForm({ ...form, production_type: v })}>
              <SelectTrigger data-testid="cr-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Country *">
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger data-testid="cr-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Filming location (town, city or postcode) *"><Input required {...inp("location")} placeholder="e.g. Manchester, M1" data-testid="cr-location-input" /></F>
          <F label="Shoot date(s) *"><Input required {...inp("shoot_dates")} placeholder="e.g. 12–14 March 2027" data-testid="cr-dates-input" /></F>
          <F label="Number of extras needed *"><Input required type="number" min="1" max="5000" {...inp("extras_needed")} data-testid="cr-number-input" /></F>
          <F label="Pay rate (optional)"><Input {...inp("pay_rate")} placeholder="e.g. £100 per day" data-testid="cr-pay-input" /></F>
          <F label="Need replies by (optional)"><Input {...inp("respond_by")} placeholder="e.g. by Friday lunchtime" data-testid="cr-respond-by-input" /></F>
          <F label="Reply-by date (optional)"><Input type="date" min={new Date().toLocaleDateString("en-CA")} className="[color-scheme:dark]" {...inp("respond_by_date")} data-testid="cr-respond-by-date-input" /><p className="text-xs text-white/40 mt-1">Members who haven't replied get a reminder the day before.</p></F>
          <F label="Who are you looking for? *" className="md:col-span-2">
            <Textarea required minLength={5} rows={6} {...inp("requirements")} placeholder="Ages, looks, genders, skills, costume/period, own car, etc." data-testid="cr-requirements-input" />
          </F>
        </section>
        <p className="text-xs text-white/40">Your contact details are only seen by the extras.tv team, not by members. See our Privacy Notice.</p>
        <button disabled={busy} className="bg-primary text-primary-foreground px-10 py-4 font-bold uppercase text-sm tracking-wider hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="cr-submit-button">
          {busy ? "Sending…" : "Send casting request"}
        </button>
      </form>
    </div>
  );
}
